/**
 * Radar minimap. Pure: reads state, writes pixels into the given rectangle.
 *
 * The minimap is one of the few render features users actually look at —
 * if anyone gets paged for a regression it'll probably be "the radar
 * shows the wrong tile colour". Tests check the mapping math by inspecting
 * pixel-position calls on a stub ctx, not real pixels.
 */

import type { GameState } from '../sim/types.ts';
import { TILE } from '../sim/maze.ts';
import { toTile } from '../sim/fixed.ts';

const COLOR = {
  bg: 'rgba(6, 6, 12, 0.85)',
  border: '#293a64',
  wall: '#1a2742',
  corridor: '#0d1224',
  player: '#7aa2f7',
  enemy: '#f38ba8',
  enemyStunned: '#6c7086',
  flagYellow: '#f7c873',
  flagDoubler: '#f78fb3',
  flagFuel: '#a6e3a1',
  flagBoost: '#fab387',
} as const;

function radarFlagColor(kind: 'yellow' | 'doubler' | 'fuel_bonus' | 'speed_boost'): string {
  switch (kind) {
    case 'doubler': return COLOR.flagDoubler;
    case 'fuel_bonus': return COLOR.flagFuel;
    case 'speed_boost': return COLOR.flagBoost;
    case 'yellow':
    default: return COLOR.flagYellow;
  }
}

export type RadarRect = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

export function computeRadarTilePx(maze: { width: number; height: number }, rect: RadarRect): number {
  // Each maze tile gets at least 1px; pick the largest size that fits.
  const sx = Math.floor(rect.width / maze.width);
  const sy = Math.floor(rect.height / maze.height);
  return Math.max(1, Math.min(sx, sy));
}

export function drawRadar(ctx: CanvasRenderingContext2D, state: GameState, rect: RadarRect): void {
  const tilePx = computeRadarTilePx(state.maze, rect);
  const drawnW = tilePx * state.maze.width;
  const drawnH = tilePx * state.maze.height;
  // Center the minimap inside its rect (avoids hugging one edge when the
  // maze aspect ratio doesn't match the rect).
  const ox = rect.x + Math.floor((rect.width - drawnW) / 2);
  const oy = rect.y + Math.floor((rect.height - drawnH) / 2);

  // Background panel
  ctx.fillStyle = COLOR.bg;
  ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  ctx.strokeStyle = COLOR.border;
  ctx.lineWidth = 1;
  ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.width - 1, rect.height - 1);

  // Maze tiles
  for (let ty = 0; ty < state.maze.height; ty++) {
    for (let tx = 0; tx < state.maze.width; tx++) {
      const kind = state.maze.tiles[ty * state.maze.width + tx];
      ctx.fillStyle = kind === TILE.WALL ? COLOR.wall : COLOR.corridor;
      ctx.fillRect(ox + tx * tilePx, oy + ty * tilePx, tilePx, tilePx);
    }
  }

  // Flags
  for (const flag of state.flags) {
    if (flag.collected) continue;
    ctx.fillStyle = radarFlagColor(flag.kind);
    ctx.fillRect(ox + flag.tile.x * tilePx, oy + flag.tile.y * tilePx, tilePx, tilePx);
  }

  // Enemies
  for (const enemy of state.enemies) {
    const tx = toTile(enemy.car.x);
    const ty = toTile(enemy.car.y);
    ctx.fillStyle = state.tick < enemy.stunUntil ? COLOR.enemyStunned : COLOR.enemy;
    ctx.fillRect(ox + tx * tilePx, oy + ty * tilePx, tilePx, tilePx);
  }

  // Player
  const ptx = toTile(state.player.x);
  const pty = toTile(state.player.y);
  ctx.fillStyle = COLOR.player;
  ctx.fillRect(ox + ptx * tilePx, oy + pty * tilePx, tilePx, tilePx);
}
